/**
 * Admin Learned Rules sub-section ( item 4).
 *
 * Create-rule-from-deficiency workflow: generate -> draft editor -> publish.
 * Per-rule: version history, enable/disable, test-run on a case, run-suite
 * confusion matrix, test-case marking.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Plus, Save, Trash2, Play, FlaskConical,
  CheckCircle2, ChevronDown, ChevronUp, AlertTriangle, Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToastStore } from '../../stores/toast';
import { listCases, type Case } from '../../api/cases';
import {
  listLearnedRules,
  createDraftVersion,
  editDraftVersion,
  publishDraftVersion,
  setLearnedRuleEnabled,
  deleteLearnedRule,
  testRunVersion,
  recordTestCase,
  runSuite,
  generateFromFeedback,
  type LearnedRule,
  type LearnedRuleVersion,
  type Severity,
  type SingleTestRunResult,
  type SuiteVersionResult,
  type CandidateDraft,
} from '../../api/learnedRules';
import {
  CreateRulePanel,
  CaseSelect,
  SEVERITIES,
} from '../learned-rules/CreateRulePanel';

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-amber-100 text-amber-700',
  active: 'bg-green-100 text-green-700',
  retired: 'bg-slate-200 text-slate-500',
};

/* ---------- Rule card ---------- */
function RuleCard({ rule, cases, casesLoading, onChanged }: {
  rule: LearnedRule;
  cases: Case[];
  casesLoading: boolean;
  onChanged: () => void;
}) {
  const { addToast } = useToastStore();
  const [expanded, setExpanded] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testCaseId, setTestCaseId] = useState('');
  const [testRun, setTestRun] = useState<SingleTestRunResult | null>(null);
  const [testRunning, setTestRunning] = useState(false);
  const [suite, setSuite] = useState<SuiteVersionResult | null>(null);
  const [suiteRunning, setSuiteRunning] = useState(false);
  const [editingDraft, setEditingDraft] = useState<LearnedRuleVersion | null>(null);
  const [editForm, setEditForm] = useState<{ detection_prompt: string; severity: Severity; points: number } | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const activeVersion = rule.active_version ?? null;
  const versions = rule.versions ?? [];

  const handleToggle = async () => {
    setToggling(true);
    try {
      await setLearnedRuleEnabled(rule.id, !rule.enabled);
      addToast(`${rule.code} ${!rule.enabled ? 'enabled' : 'disabled'}`, 'success');
      onChanged();
    } catch {
      addToast('Failed to toggle rule', 'error');
    } finally {
      setToggling(false);
    }
  };

  const handlePublish = async (v: LearnedRuleVersion) => {
    try {
      await publishDraftVersion(rule.id, v.version_no);
      addToast(`Version ${v.version_no} published`, 'success');
      onChanged();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Publish failed';
      addToast(msg, 'error');
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete learned rule ${rule.code}? This cascades versions + test cases.`)) return;
    try {
      await deleteLearnedRule(rule.id, true);
      addToast(`Rule ${rule.code} deleted`, 'success');
      onChanged();
    } catch {
      addToast('Failed to delete rule', 'error');
    }
  };

  const handleTestRun = async (versionId: string) => {
    if (!testCaseId) { addToast('Select a case first', 'error'); return; }
    setTestRunning(true);
    setTestRun(null);
    try {
      const r = await testRunVersion(versionId, testCaseId);
      setTestRun(r);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Test-run failed';
      addToast(msg, 'error');
    } finally {
      setTestRunning(false);
    }
  };

  const handleMarkCase = async (versionId: string, expected: 'should_trigger' | 'should_not_trigger') => {
    if (!testCaseId) { addToast('Select a case first', 'error'); return; }
    try {
      await recordTestCase(versionId, testCaseId, expected);
      addToast(`Case marked ${expected === 'should_trigger' ? 'should-trigger' : 'should-not-trigger'}`, 'success');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Mark failed';
      addToast(msg, 'error');
    }
  };

  const handleRunSuite = async (versionId: string) => {
    setSuiteRunning(true);
    setSuite(null);
    try {
      const results = await runSuite([versionId]);
      setSuite(results[0] ?? null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Suite failed';
      addToast(msg, 'error');
    } finally {
      setSuiteRunning(false);
    }
  };

  const startEditDraft = (v: LearnedRuleVersion) => {
    setEditingDraft(v);
    setEditForm({ detection_prompt: v.detection_prompt, severity: v.severity, points: v.points });
  };

  const saveEditDraft = async () => {
    if (!editingDraft || !editForm) return;
    setSavingDraft(true);
    try {
      await editDraftVersion(rule.id, editingDraft.version_no, editForm);
      addToast(`Draft v${editingDraft.version_no} saved`, 'success');
      setEditingDraft(null);
      onChanged();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Edit failed';
      addToast(msg, 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleNewDraft = async () => {
    if (!activeVersion) { addToast('No active version to branch from', 'error'); return; }
    try {
      await createDraftVersion(rule.id, {
        detection_prompt: activeVersion.detection_prompt,
        target_data: activeVersion.target_data,
        severity: activeVersion.severity,
        points: activeVersion.points,
        evidence_fields: activeVersion.evidence_fields,
        negative_examples: activeVersion.negative_examples,
        rationale: activeVersion.rationale ?? undefined,
      });
      addToast('New draft version created from active', 'success');
      onChanged();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to create draft';
      addToast(msg, 'error');
    }
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-slate-600 shrink-0"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-slate-800">{rule.code}</span>
              <span className="text-sm text-slate-600 truncate">{rule.name}</span>
              {activeVersion && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                  Active v{activeVersion.version_no}
                </span>
              )}
              {!rule.enabled && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-200 text-slate-500">disabled</span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">Cat {rule.category} - {versions.length} version(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleToggle}
            disabled={toggling}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50"
            style={{ backgroundColor: rule.enabled ? '#16a34a' : '#cbd5e1' }}
            role="switch"
            aria-checked={rule.enabled}
            aria-label={`Toggle ${rule.code}`}
          >
            {toggling ? (
              <Loader2 className="absolute left-1 animate-spin text-white" size={14} />
            ) : (
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${rule.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            )}
          </button>
          <button onClick={handleDelete} className="text-slate-400 hover:text-red-600" aria-label="Delete rule">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-slate-100 pt-3 space-y-4">
          {/* Version history */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-600 uppercase">Version history</h4>
              {activeVersion && (
                <button onClick={handleNewDraft}
                  className="btn-secondary text-xs flex items-center gap-1 px-2 py-1">
                  <Plus size={12} /> New draft from active
                </button>
              )}
            </div>
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-slate-700">v{v.version_no}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_BADGE[v.status] ?? 'bg-slate-100'}`}>
                        {v.status}
                      </span>
                      <span className="text-xs text-slate-500">{v.severity} - {v.points} pts - {v.target_data}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {v.status === 'draft' && (
                        <>
                          <button onClick={() => startEditDraft(v)}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium">Edit</button>
                          <button onClick={() => handlePublish(v)}
                            className="text-xs text-green-600 hover:text-green-700 font-medium">Publish</button>
                        </>
                      )}
                    </div>
                  </div>
                  {editingDraft?.id === v.id && editForm ? (
                    <div className="mt-2 space-y-2 bg-white rounded p-2 border border-slate-200">
                      <div className="grid grid-cols-2 gap-2">
                        <select className="input text-xs" value={editForm.severity}
                          onChange={(e) => setEditForm({ ...editForm, severity: e.target.value as Severity })}>
                          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <input type="number" min={1} max={5} className="input text-xs"
                          value={editForm.points}
                          onChange={(e) => setEditForm({ ...editForm, points: parseInt(e.target.value, 10) || 1 })} />
                      </div>
                      <textarea className="input text-xs" rows={4} value={editForm.detection_prompt}
                        onChange={(e) => setEditForm({ ...editForm, detection_prompt: e.target.value })} />
                      <div className="flex items-center gap-2">
                        <button onClick={saveEditDraft} disabled={savingDraft}
                          className="btn-primary text-xs px-3 py-1 flex items-center gap-1 disabled:opacity-50">
                          {savingDraft ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save
                        </button>
                        <button onClick={() => setEditingDraft(null)}
                          className="btn-secondary text-xs px-3 py-1">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-3">{v.detection_prompt}</p>
                  )}
                  {v.negative_examples.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-xs text-slate-400 cursor-pointer">Negative examples ({v.negative_examples.length})</summary>
                      <ul className="text-xs text-slate-500 mt-1 list-disc list-inside">
                        {v.negative_examples.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
              {versions.length === 0 && <p className="text-xs text-slate-400">No versions.</p>}
            </div>
          </div>

          {/* Test-run + test-case marking */}
          <div className="rounded-lg border border-slate-200 p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase mb-2 flex items-center gap-1">
              <Play size={12} /> Test run + test-case marking
            </h4>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col">
                <label className="text-xs font-medium text-slate-500 mb-1">Case</label>
                <CaseSelect value={testCaseId} onChange={setTestCaseId} cases={cases} loading={casesLoading} />
              </div>
              {activeVersion && (
                <>
                  <button onClick={() => handleTestRun(activeVersion.id)} disabled={testRunning}
                    className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
                    {testRunning ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />} Test run
                  </button>
                  <button onClick={() => handleMarkCase(activeVersion.id, 'should_trigger')}
                    className="text-xs px-2 py-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50">
                    Mark should-trigger
                  </button>
                  <button onClick={() => handleMarkCase(activeVersion.id, 'should_not_trigger')}
                    className="text-xs px-2 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">
                    Mark should-not-trigger
                  </button>
                  <button onClick={() => handleRunSuite(activeVersion.id)} disabled={suiteRunning}
                    className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
                    {suiteRunning ? <Loader2 className="animate-spin" size={12} /> : <FlaskConical size={12} />} Run suite
                  </button>
                </>
              )}
            </div>

            {testRun && (
              <div className="mt-3 p-3 rounded bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    testRun.status === 'triggered' ? 'bg-red-100 text-red-700'
                    : testRun.status === 'clean' ? 'bg-green-100 text-green-700'
                    : testRun.status === 'not_verifiable' ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-200 text-slate-600'
                  }`}>{testRun.status}</span>
                  {testRun.confidence != null && (
                    <span className="text-xs text-slate-500">confidence: {(testRun.confidence * 100).toFixed(0)}%</span>
                  )}
                  {testRun.error && <span className="text-xs text-red-600">{testRun.error}</span>}
                </div>
                <p className="text-xs text-slate-600 mt-1">{testRun.evidence}</p>
                {testRun.citation && <p className="text-xs text-slate-500 mt-1">Source: {testRun.citation}</p>}
              </div>
            )}

            {suite && (
              <div className="mt-3 p-3 rounded bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  <span className="font-semibold text-slate-700">Confusion: v{suite.version_no}</span>
                  <span className="text-green-700">TP {suite.tp}</span>
                  <span className="text-red-700">FP {suite.fp}</span>
                  <span className="text-slate-600">TN {suite.tn}</span>
                  <span className="text-amber-700">FN {suite.fn}</span>
                  {suite.indeterminate > 0 && <span className="text-slate-500">? {suite.indeterminate}</span>}
                  <span className="text-slate-500">precision: {suite.precision == null ? '-' : (suite.precision * 100).toFixed(0)}%</span>
                  <span className="text-slate-500">recall: {suite.recall == null ? '-' : (suite.recall * 100).toFixed(0)}%</span>
                </div>
                {suite.cases.length > 0 && (
                  <table className="min-w-full text-xs mt-2 border border-slate-200 rounded">
                    <thead><tr className="bg-slate-100">
                      <th className="border border-slate-200 p-1.5 text-left">Case</th>
                      <th className="border border-slate-200 p-1.5">Expected</th>
                      <th className="border border-slate-200 p-1.5">Actual</th>
                      <th className="border border-slate-200 p-1.5">Outcome</th>
                    </tr></thead>
                    <tbody>
                      {suite.cases.map((c, i) => (
                        <tr key={i}>
                          <td className="border border-slate-200 p-1.5 font-mono">{c.case_id.slice(0, 8)}</td>
                          <td className="border border-slate-200 p-1.5 text-center">{c.expected}</td>
                          <td className="border border-slate-200 p-1.5 text-center">{c.actual}</td>
                          <td className="border border-slate-200 p-1.5 text-center font-semibold">{c.outcome}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- generate candidates from feedback ---------- */
function GenerateFromFeedbackPanel({ onGenerated }: { onGenerated: () => void }) {
  const { addToast } = useToastStore();
  const [running, setRunning] = useState(false);
  const [candidates, setCandidates] = useState<CandidateDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setRunning(true);
    setError(null);
    try {
      const items = await generateFromFeedback();
      setCandidates(items);
      if (items.length === 0) {
        addToast('No feedback patterns found to generate drafts from.', 'info');
      } else {
        addToast(`Generated ${items.length} draft candidate(s) from feedback.`, 'success');
        onGenerated();
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to aggregate feedback.';
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="text-violet-600" size={16} />
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Generate rule candidates from feedback</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              On-demand: aggregates reviewer false-positive feedback + missed-abnormality notes
              into DRAFT rules with precision/recall. Human review required to publish.
            </p>
          </div>
        </div>
        <button onClick={handleGenerate} disabled={running}
          className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1 disabled:opacity-50">
          {running ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
          {running ? 'Generating...' : 'Generate'}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <AlertTriangle className="shrink-0 mt-0.5" size={14} />
          <span>{error}</span>
        </div>
      )}

      {candidates.length > 0 && (
        <div className="space-y-2">
          {candidates.map((c) => (
            <div key={c.version_id} className="rounded-lg border border-violet-200 bg-violet-50/50 p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-semibold text-violet-800">{c.rule_code}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">draft v{c.version_no}</span>
                <span className="text-xs text-slate-500">
                  precision: {c.precision == null ? '-' : (c.precision * 100).toFixed(0)}%
                </span>
                <span className="text-xs text-slate-500">
                  recall: {c.recall == null ? '-' : (c.recall * 100).toFixed(0)}%
                </span>
                <span className="text-xs text-slate-500">
                  TP {c.tp} / FP {c.fp} / TN {c.tn} / FN {c.fn}
                </span>
              </div>
              <p className="text-xs text-slate-700 mt-1">{c.deficiency_description}</p>
              <p className="text-xs text-slate-500 mt-1">
                Example cases: {c.example_case_ids.map((id) => id.slice(0, 8)).join(', ') || 'none'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Review + publish in the rules list below (expand the rule, then Publish).
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Main tab ---------- */
export default function LearnedRulesTab() {
  const [rules, setRules] = useState<LearnedRule[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [casesLoading, setCasesLoading] = useState(true);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await listLearnedRules());
    } catch {
      // non-fatal toast handled by callers
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const resp = await listCases({ page: 1, page_size: 100 });
      setCases(resp.items);
    } catch {
      setCases([]);
    } finally {
      setCasesLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); loadCases(); }, [loadRules, loadCases]);

  const reload = useCallback(() => { loadRules(); }, [loadRules]);

  const activeCount = useMemo(() => rules.filter((r) => r.enabled && r.active_version).length, [rules]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-3 bg-indigo-50 border-indigo-200">
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-indigo-600 shrink-0" size={16} />
          <p className="text-xs text-indigo-800">
            Learned rules are AI-generated, versioned, + separately stored from the
            hardcoded rubric. Active + enabled rules merge into review scoring. {activeCount} active.
          </p>
        </div>
      </div>

      <CreateRulePanel cases={cases} casesLoading={casesLoading} onCreated={reload} />

      <GenerateFromFeedbackPanel onGenerated={reload} />

      {!casesLoading && cases.length === 0 && (
        <div className="card p-4 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
            <div className="text-xs text-amber-800">
              <p className="font-semibold">No cases yet</p>
              <p className="mt-0.5">
                The learned-rules workflow needs at least one case to generate a
                rule, test-run, or run a suite.{' '}
                <Link to="/cases/new" className="text-primary-700 hover:underline font-medium">
                  Create a case on the Cases page
                </Link>{' '}
                or seed demo data to get started.
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-800">Rules</h3>
          <button onClick={reload} className="text-xs text-primary-600 hover:underline">Refresh</button>
        </div>
        <div className="space-y-2">
          {rules.map((r) => (
            <RuleCard key={r.id} rule={r} cases={cases} casesLoading={casesLoading} onChanged={reload} />
          ))}
          {rules.length === 0 && (
            <div className="card p-6 text-center">
              <CheckCircle2 className="mx-auto text-slate-300" size={28} />
              <p className="text-sm text-slate-400 mt-2">No learned rules yet. Create one from a deficiency above.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}